import Divider from "@mui/material/Divider";
import InputAdornment from "@mui/material/InputAdornment";
import Typography from "@mui/material/Typography";

import { ProfileType } from "core/models/profile";

import Box from "ui/atom/Box";
import Form from "ui/atom/Form";
import Icon from "ui/atom/Icon";
import Button from "ui/base/Button";
import Card from "ui/base/Card";
import Input from "ui/base/Input";
import Page from "ui/Page";
import Table from "ui/Table";

import { useSelectedAccount, useCards, useDeposits, useHistory, useUser, useUserInfo, useProfileInfo, useCampaigns } from "../fabric";
import Search from "../components/Search";


type SectionProps = {
	title: string;
	children: React.ReactNode;
	actions?: boolean;
	empty?: boolean;
	emptyText?: string;
	loading: boolean;
	onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void;
};

Users.Section = ({ title, actions, children, onSubmit, loading, empty, emptyText }: SectionProps) => {
	const style = {
		root: {
			flex: "1 1 300px",
			display: "flex",
			flexDirection: "column"
		},
		actions: { p: 2, mt: "auto", justifyContent: "flex-end" }
	} as StyleProps;

	return (
		<Card sx={style.root}>
			<Card.Header title={title} />
			<Card.Content>
				<Form id={`form-${title}`} onSubmit={onSubmit}>
					<Box.Column gap={1}>
						{empty
							? <Typography variant="h4" sx={{ textAlign: "center", mt: 4 }}>{emptyText || "No data available"}</Typography>
							: children
						}
					</Box.Column>
				</Form>
			</Card.Content>
			{!empty && (
				<Card.Actions sx={style.actions}>
					<Box gap={1} sx={{ justifyContent: "flex-end", mt: 2 }}>
						{actions && <Button.Menu variant="contained" color="secondary" selected={0} select={(v) => console.log(v)} display="Quick action" options={[{ label: "Reset Unlocks", value: 0 }, { label: "Reset Deposits", value: 1 }]} />}
						<Button form={`form-${title}`} type="submit" variant="contained" color="success" loading={loading}>Save</Button>
						<Button form={`form-${title}`} type="reset" variant="contained" color="error" disabled={loading}>Reset</Button>
					</Box>
				</Card.Actions>
			)}
		</Card>
	);
};

const UserTable = ({ title, children }: { title: string; children?: React.ReactNode }) => (
	<Box.Column gap={2} sx={{ mt: 2, flex: "1 1 250px" }}>
		<Typography variant="h4">{title}</Typography>
		{children}
	</Box.Column>
);

Users.Cards = () => {
	const cards = useCards();
	return (
		<UserTable title="Cards">
			<Table columns={cards.columns} data={cards.data} icon="list-todo" height={50} loading={cards.isPending} />
		</UserTable>
	);
};

Users.Deposits = () => {
	const deposits = useDeposits();
	return (
		<UserTable title="Deposits">
			<Table columns={deposits.columns} data={deposits.data?.values} icon="list-todo" height={50} loading={deposits.isPending} />
		</UserTable>
	);
};

Users.History = () => {
	const history = useHistory();
	return (
		<UserTable title="History">
			<Table columns={history.columns} data={history.data?.values} icon="list-todo" height={58} loading={history.isPending} />
		</UserTable>
	);
};

Users.Campaigns = () => {
	const campaigns = useCampaigns();
	return (
		<UserTable title="Campaigns">
			<Table columns={campaigns.columns} data={campaigns.data} icon="list-todo" height={58} loading={campaigns.isPending} />
		</UserTable>
	);
};

Users.Info = () => {
	const { data, form, disabled, isLoading, accountMutation } = useUserInfo();

	return (
		<Users.Section title="User Info" loading={form.state.isSubmitting} onSubmit={form.handleSubmit}>
			{!data && isLoading
				? <Typography variant="h4" sx={{ textAlign: "center", mt: 4 }}>Loading user info...</Typography>
				: (
					<form.AppForm>
						<form.AppField name="name" children={(field) => <field.Input label="Name" />} />
						<form.AppField name="nickname" children={(field) => <field.Input label="Nickname" />} />

						<Box gap={1} sx={{ alignItems: "flex-end" }}>
							<form.AppField name="email" children={(field) => <field.Input label="Email" type="email" disabled />} />
							<Button.Confirmation
								icon="circle-x"
								color="error"
								title="Remove Email"
								text="Are you sure you want to remove the email from this user?"
								disabled={disabled}
								onClick={() => accountMutation.mutateAsync({ email: data?.email?.value })}
								sx={{ mb: 0.5 }}
							/>
						</Box>
						<Box gap={1} sx={{ alignItems: "flex-end" }}>
							<form.AppField name="phone" children={(field) => <field.Phone label="Phone" disabled />} />

							<Button.Confirmation
								icon="circle-x"
								color="error"
								title="Remove Phone"
								text="Are you sure you want to remove the phone number from this user?"
								disabled={disabled}
								onClick={() => accountMutation.mutateAsync({ phone: data?.phone?.value })}
								sx={{ mb: 0.5 }}
							/>
						</Box>
					</form.AppForm>
				)}
		</Users.Section>
	);
};

Users.Profile = () => {
	const { form, profile, mutation, reload } = useProfileInfo();

	return (
		<Users.Section title={`Profile - ${profile?.type}`} empty={!profile} emptyText="Please select a profile to view details" loading={mutation.isPending} actions onSubmit={form.handleSubmit}>
			<form.AppForm>
				<form.AppField name="type" children={(field) => <field.Enum enumType={ProfileType} label="Type" />} />
				<form.AppField name="balance" children={(field) =>
					<field.Input
						type="number"
						label="Balance"
						startAdornment={<InputAdornment position="start"><Icon.Coin size={16} /></InputAdornment>}
					/>}
				/>
				<Box gap={1}>
					<form.AppField name="reload" children={(field) => <field.Select options={reload.options.amount} label="Reload" />} />
					<form.AppField name="threshold" children={(field) => <field.Select options={reload.options.threshold} label="Threshold" />} />
				</Box>
			</form.AppForm>
		</Users.Section>
	);
};

Users.Search = () => {
	const user = useUser();
	const selectedUser = useSelectedAccount();

	return (
		<Box gap={2}>
			<Search value={user.user} setValue={user.setUser} />
			<Input.Select
				label="Select profile"
				value={user.profileId || selectedUser.profile?.id.toString() || ""}
				onChange={(e) => user.setProfile(e.target.value as string)}
				disabled={!user.user.id}
				fullWidth
				options={selectedUser.data?.profiles?.map(p => ({ value: p.id.toString(), label: p.type })) || []}
			/>
		</Box>
	);
};

Users.Form = () => {
	const user = useUser();

	if (!user.user.id) return <Typography variant="h4" sx={{ textAlign: "center", mt: 4 }}>Please select a user to view details</Typography>;
	return (
		<Box.Column gap={1}>
			<Box gap={2} sx={{ flexWrap: "wrap" }}>
				<Users.Info />
				<Users.Profile />
			</Box>
			<Divider sx={{ my: 2 }} />
			<Box gap={2} sx={{ width: "100%", flexWrap: "wrap" }}>
				<Users.Cards />
				<Users.Deposits />
			</Box>
			<Users.History />
			<Users.Campaigns />
		</Box.Column>
	);
};

export default function Users() {
	return (
		<Page>
			<Page.Header title="Users" />
			<Page.Body>
				<Users.Search />
				<Divider sx={{ my: 2 }} />
				<Users.Form />
			</Page.Body>
		</Page>
	);
}